import { useState, useCallback, ReactNode, useRef, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import CheckIcon from "@sprout/icons/check";
import ErrorIcon from "@sprout/icons/error";
import { useEffectAfterMount } from "@/hooks/use_effect_after_mount";

type FlashType = "success" | "error";

interface FlashMessage {
  id: number;
  message: string;
  type: FlashType;
  fadingOut?: boolean;
}

export interface RailsFlash {
  error?: string;
  success?: string;
}

type ShowFlashContextValue = (message: string, type?: FlashType) => void;

const FLASH_DURATION = 4000;
const FLASH_FADE_DURATION = 300;

const ShowFlashContext = createContext<ShowFlashContextValue>(() => {
  throw new Error("useShowFlash should only be called inside a ShowFlashProvider");
});

export const useShowFlash = () => useContext(ShowFlashContext);

export const useRailsFlash = (flash: RailsFlash) => {
  const flashed = useRef(false);
  const showFlash = useShowFlash();

  useEffectAfterMount(() => {
    if (flashed.current) {
      return;
    }

    if (flash?.error) {
      showFlash(flash.error, "error");
      flashed.current = true;
    }

    if (flash?.success) {
      showFlash(flash.success, "success");
      flashed.current = true;
    }
  });
};

export const ShowFlashProvider = ({ children }: { children: ReactNode }) => {
  const [messages, setMessages] = useState<FlashMessage[]>([]);

  // Assign to the global function
  const showFlash = useCallback((message: string, type: FlashType = "success") => {
    const id = Date.now();

    setMessages((prev) => [...prev, { id, message, type }]);

    setTimeout(() => {
      setMessages((prev) => {
        return prev.map((msg) => {
          return msg.id === id ? { ...msg, fadingOut: true } : msg;
        });
      });
    }, FLASH_DURATION - FLASH_FADE_DURATION);

    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    }, FLASH_DURATION);
  }, []);

  const mountElement = window.document.getElementById("app");

  if (!mountElement) {
    return null;
  }

  return (
    <ShowFlashContext.Provider value={showFlash}>
      {children}
      {createPortal(
        <div className="flash-wrapper">
          {messages.map(({ id, message, type, fadingOut }) => (
            <div
              key={id}
              className={classNames("flash-message", {
                "flash-message--success": type === "success",
                "flash-message--error": type === "error",
                "fade-out": fadingOut,
              })}
            >
              <span>
                {type === "error" ? <ErrorIcon color="red" /> : <CheckIcon color="green" />}
              </span>
              <div className="flash-body">{message}</div>
            </div>
          ))}
        </div>,
        mountElement,
      )}
    </ShowFlashContext.Provider>
  );
};
